import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api, { pickErr } from "@/lib/api";

const ADMIN_KEY = "saarthi_admin";
const STUDENT_KEY = "saarthi_student";

const AuthContext = createContext(null);

function readJson(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(() => readJson(ADMIN_KEY));
  const [student, setStudent] = useState(() => readJson(STUDENT_KEY));

  useEffect(() => {
    if (admin) localStorage.setItem(ADMIN_KEY, JSON.stringify(admin));
    else localStorage.removeItem(ADMIN_KEY);
  }, [admin]);

  useEffect(() => {
    if (student) localStorage.setItem(STUDENT_KEY, JSON.stringify(student));
    else localStorage.removeItem(STUDENT_KEY);
  }, [student]);

  async function loginAdmin(email, password) {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // data: { access_token, role, school_id? }
      setAdmin({
        access_token: data.access_token,
        role: data.role,
        school_id: data.school_id || null,
        email,
      });
      return { ok: true, role: data.role };
    } catch (err) {
      return { ok: false, error: pickErr(err, "Login failed") };
    }
  }

  async function loginStudent(student_code, dob) {
    try {
      const { data } = await api.post("/student/login", { student_code, dob });
      setStudent({ student_id: data.student_id, student_code, dob });
      return { ok: true, student_id: data.student_id };
    } catch (err) {
      return { ok: false, error: pickErr(err, "Login failed") };
    }
  }

  function logout() {
    setAdmin(null);
    setStudent(null);
  }

  const value = useMemo(
    () => ({ admin, student, loginAdmin, loginStudent, logout }),
    [admin, student]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
